"use client";

import { useEffect, useId, useRef, useState } from "react";

// Richtext comes in as server-rendered children so no richtext renderer ships
// to the client. Animates maxHeight using the measured content height.
export default function ExpandableText({
  children,
  fadeHex,
  collapsedHeightPx,
  collapsedHeightPxMobile,
  expandLabel,
  collapseLabel,
}: {
  children: React.ReactNode;
  fadeHex: string;
  collapsedHeightPx: number;
  collapsedHeightPxMobile: number;
  expandLabel: string;
  collapseLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [fullHeight, setFullHeight] = useState<number | null>(null);
  const [collapsedHeight, setCollapsedHeight] = useState(
    collapsedHeightPxMobile,
  );
  const id = useId();
  const innerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = innerRef.current;
    if (!node) return;
    const measure = () => {
      setFullHeight(node.scrollHeight);
      const isDesktop = window.matchMedia("(min-width: 1115px)").matches;
      setCollapsedHeight(
        isDesktop ? collapsedHeightPx : collapsedHeightPxMobile,
      );
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [collapsedHeightPx, collapsedHeightPxMobile]);

  const showFade = !open && fullHeight !== null && fullHeight > collapsedHeight;
  const maxHeight = open
    ? fullHeight ?? collapsedHeight
    : collapsedHeight;

  return (
    <div>
      <div
        id={id}
        inert={showFade}
        className="relative overflow-hidden transition-[max-height] duration-500 ease-out"
        style={{ maxHeight: `${maxHeight}px` }}
      >
        <div ref={innerRef}>{children}</div>
        {showFade && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-32"
            style={{
              background: `linear-gradient(to bottom, transparent 0%, ${fadeHex} 75%)`,
            }}
          />
        )}
      </div>
      {fullHeight !== null && fullHeight > collapsedHeight && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={id}
          className="mt-4 inline-flex w-fit cursor-pointer items-center py-2.5 text-body font-extrabold leading-[1.054] text-purple-04 hover:underline"
        >
          {open ? collapseLabel : expandLabel}
        </button>
      )}
    </div>
  );
}
